import { writeBatch } from "firebase/firestore";
// Client-side counterpart to `functions/src/batch.ts`. The two cannot be shared — that
// one is `firebase-admin` and bound to the functions `db` singleton, this one is the
// `firebase` client SDK, and the `WriteBatch` / `DocumentReference` types are distinct
// between them. The chunking rule is the same; the commit semantics are not.
//
// A WriteBatch is capped at 500 operations, so any write whose size follows user data
// (a whole template, a provenance patch per trip row) has to chunk. Enqueue with
// `set` / `update` / `delete`, then `await commit()`.
//
// IMPORTANT — two properties callers depend on, both differing from the admin version:
//
//   1. **Chunks commit sequentially, in enqueue order.** The admin version fires its
//      chunks concurrently and lets a `retry: true` trigger converge afterwards. On the
//      client there is no retry harness, and ordering across chunks is observable to
//      anyone streaming the collections — so an applier that enqueues rows before the
//      categories holding them keeps that order on the wire.
//   2. **It is NOT atomic across chunks.** Firestore offers no all-or-nothing commit
//      beyond one batch, so a failure part-way leaves earlier chunks applied. `commit()`
//      rejects on the first failing chunk and does not attempt the rest. Only enqueue
//      IDEMPOTENT writes (overwrite-set / delete, never increments), so that re-running
//      the whole operation converges.
const LIMIT = 450;
export class ChunkedBatch {
    newBatch;
    // Buffered as thunks rather than written into a live batch, because the enqueue
    // methods are synchronous and flushing eagerly could not await the commit — which is
    // exactly how the admin version ends up concurrent.
    ops = [];
    constructor(newBatch) {
        this.newBatch = newBatch;
    }
    static forDb(db) {
        return new ChunkedBatch(() => writeBatch(db));
    }
    set(ref, data, merge = false) {
        this.ops.push((b) => {
            b.set(ref, data, merge ? { merge: true } : {});
        });
    }
    update(ref, data) {
        this.ops.push((b) => {
            b.update(ref, data);
        });
    }
    delete(ref) {
        this.ops.push((b) => {
            b.delete(ref);
        });
    }
    /** Number of enqueued writes — the count the chunking applies to. */
    get size() {
        return this.ops.length;
    }
    async commit() {
        for (let i = 0; i < this.ops.length; i += LIMIT) {
            const batch = this.newBatch();
            for (const op of this.ops.slice(i, i + LIMIT))
                op(batch);
            await batch.commit();
        }
    }
}
//# sourceMappingURL=chunkedBatch.js.map